<template>
  <el-dialog
    :model-value="isOpen"
    width="844px"
    class="tw-overflow-hidden"
    :style="`max-height: calc(100vh - ${selectedCompany ? '10vh' : '15vh' })`"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <div class="tw-flex tw-items-center tw-mb-8">
      <i-button
        v-if="selectedCompany"
        size="medium"
        class="tw-mr-4"
        is-icon
        @click="selectedCompany = null"
      >
        <icon-angle-left />
      </i-button>
      <h1 class="tw-tracking-wider">
        {{ $t('adm.companies.company_dialog.title_new') }}
      </h1>
    </div>

    <template v-if="!selectedCompany">
      <el-form
        ref="SearchForm"
        :model="searchForm"
        :rules="searchFormRules"
        label-position="top"
        :disabled="isLoading"
        class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
        @submit.native.prevent="searchForCompaniesByNameOrIco"
        @keydown.enter.native.prevent="searchForCompaniesByNameOrIco"
      >
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.name')"
            :class="searchForm.name ? 'has-value' : ''"
            prop="name"
          >
            <el-input
              v-model="searchForm.name"
            />
          </i-form-item>
          <i-form-item
            :label="$t('adm.companies.company_dialog.ico')"
            :class="searchForm.ico ? 'has-value' : ''"
            prop="ico"
          >
            <el-input
              v-model="searchForm.ico"
            />
          </i-form-item>
        </div>
      </el-form>

      <div
        v-if="companiesSearchResult != null && companiesSearchResult.length > 0"
        class="tw-mt-3 tw-overflow-y-auto"
        style="max-height: calc(100vh - 15vh - 430px)"
      >
        <AdministrationTableHeader
          store-path="v2/admin/cms/clients/companies"
          filters-state-relative-path="companiesByNameOrIcoFilters"
          filters-mutation-relative-path="sortCompaniesByNameOrIco"
          :columns="columns"
        />
        <AddClientsCompaniesList
          @close="$emit('close')"
          @select-company="handleCompanySelection"
        />
      </div>
      <div
        v-else-if="companiesSearchResult != null && companiesSearchResult.length === 0"
        class="tw-mt-3"
      >
        {{ $t('adm.companies.company_dialog.search_empty') }}
      </div>
    </template>
    <template v-else>
      <el-form
        ref="CreateForm"
        :model="createForm"
        :rules="createFormRules"
        label-position="top"
        :disabled="isLoading"
        class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
        @submit.native.prevent="submitCreateCompanyForm"
        @keydown.enter.native.prevent="submitCreateCompanyForm"
      >
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.full_name')"
            :class="[
              selectedCompany.fullname ? 'has-value' : ''
            ]"
          >
            <el-input
              v-model="selectedCompany.fullname"

              disabled
            />
          </i-form-item>
        </div>
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('adm.companies.company_dialog.ico')"
            :class="[
              selectedCompany.ic_number ? 'has-value' : ''
            ]"
          >
            <el-input
              v-model="selectedCompany.ic_number"
              disabled
            />
          </i-form-item>
          <i-form-item
            :label="$t('adm.companies.company_dialog.dic')"
            :class="[
              selectedCompany.dic_number ? 'has-value' : ''
            ]"
          >
            <el-input
              v-model="selectedCompany.dic_number"
              disabled
            />
          </i-form-item>
        </div>
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('adm.companies.company_dialog.street')"
            :class="[
              selectedCompany.address_street ? 'has-value' : ''
            ]"
          >
            <el-input
              v-model="selectedCompany.address_street"
              disabled
            />
          </i-form-item>
          <i-form-item
            :label="$t('adm.companies.company_dialog.house_number')"
            :class="[
              selectedCompany.address_house_number ? 'has-value' : ''
            ]"
          >
            <el-input
              v-model="selectedCompany.address_house_number"
              disabled
            />
          </i-form-item>
        </div>
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('adm.companies.company_dialog.city')"
            :class="[
              selectedCompany.address_city ? 'has-value' : ''
            ]"
          >
            <el-input
              v-model="selectedCompany.address_city"
              disabled
            />
          </i-form-item>
          <i-form-item
            :label="$t('adm.companies.company_dialog.country')"
            :class="[
              selectedCompanyCountry ? 'has-value' : ''
            ]"
          >
            <el-input
              v-model="selectedCompanyCountry.name"
              disabled
            />
          </i-form-item>
        </div>
        <div class="tw-flex tw-items-start tw-gap-2">
          <i-form-item
            :label="$t('common.fields.short_name')"
            prop="short_name"
          >
            <el-input
              v-model="createForm.short_name"
            />
          </i-form-item>
        </div>
      </el-form>
    </template>
    <template #footer>
      <div
        v-if="!selectedCompany"
        class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row"
      >
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          native-type="submit"
          class="tw-w-40"
          :is-loading="isLoading"
          @click="searchForCompaniesByNameOrIco"
        >
          {{ $t('common.actions.search') }}
        </i-button>
      </div>
      <div
        v-else
        class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row"
      >
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          native-type="submit"
          class="tw-w-40"
          :is-loading="isLoading"
          @click="submitCreateCompanyForm"
        >
          {{ $t('common.actions.create') }}
        </i-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { addCompaniesTableColumns } from '../../constants';
import AdministrationTableHeader from '@/components/Administration/components/AdministrationTableHeader.vue';
import AddClientsCompaniesList from '@/components/Administration/Client/Companies/AddClientsCompaniesList.vue';
import { getValidationError } from '@/helpers';

const FORM_BASE = {
  name: '',
  ico: '',
};

export default {
  name: 'AddClientsCompanyDialog',
  components: { AddClientsCompaniesList, AdministrationTableHeader },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      selectedCompany: false,
      searchForm: _cloneDeep(FORM_BASE),
      searchFormRules: {
        name: [
          { required: false, validator: this.customValidator('ico'), trigger: 'blur' },
        ],
        ico: [
          { required: false, validator: this.customValidator('name'), trigger: 'blur' },
        ],
      },
      unknownCountry: 'Unknown country',
      searchResult: null,
      createForm: {
        short_name: '',
        client_companies: [],
      },
      createFormRules: {
        short_name: [
          { required: false, trigger: 'blur' },
        ],
        client_companies: [
          { required: false, message: this.$t('common.fields.required'), trigger: 'blur' },
        ],
      },
    };
  },

  computed: {
    ...mapState({
      companiesSearchResult: (state) => state.v2.admin.cms.clients.companies.companiesByNameOrIco,
      countries: (state) => state.v2.shared.enums.enums.countries,
    }),
    columns: () => addCompaniesTableColumns,

    selectedCompanyCountry() {
      return this.countries.find((country) => country.id === this.selectedCompany.address_country_id) ?? { name: this.selectedCompany.address_country_id };
    },
  },

  methods: {
    opened() {
      this.$refs.SearchForm?.clearValidate();
      this.$refs.CreateForm?.clearValidate();
    },
    closed() {
      this.searchForm = _cloneDeep(FORM_BASE);
      this.selectedCompany = null;
      this.$refs.SearchForm?.clearValidate();
      this.$refs.CreateForm?.clearValidate();
      this.$store.dispatch('v2/admin/cms/clients/companies/resetCompaniesByNameOrIco');
    },

    handleCompanySelection(company) {
      this.selectedCompany = company;
    },

    customValidator(otherField) {
      return (rule, value, callback) => {
        if (!value && (!this.searchForm[otherField] || this.searchForm[otherField].length < 1)) {
          callback(new Error('Either name or ICO is required'));
        } else if (value && value.length < 3) {
          callback(new Error('Minimum length is 3'));
        } else {
          callback();
        }
      };
    },

    async searchForCompaniesByNameOrIco() {
      const isValid = await this.$refs.SearchForm.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        await this.$store.dispatch('v2/admin/cms/clients/companies/searchCompanyByNameOrIco', {
          payload: this.searchForm,
        });

      } catch(error) {
        const message = _get(error, 'response.data.data.relation_with_id');
        this.$message.error({
          message: this.$capitalize(message || 'Error'),
          showClose: true,
        });
      } finally {
        this.isLoading = false;
      }
    },

    async submitCreateCompanyForm() {
      const isValid = await this.$refs.CreateForm.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/admin/cms/clients/companies/addClientsCompany', {
          payload: { ...this.createForm, ...this.selectedCompany },
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('close');
      } catch(error) {
        const errorText = getValidationError(error);

        this.$message.error({
          message: errorText,
          showClose: true,
          dangerouslyUseHTMLString: true,
        });
      }
      this.isLoading = false;
    },
  },
};
</script>
